import React, {
  useMemo,
} from 'react';
import clsx from 'clsx';
import Dialog from 'pages/Dashboard/components/Dialog';
import Button from 'pages/Dashboard/components/Button';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  isEmptyRichText,
  normalizeContent,
} from 'views/EMR/Settings/helper';

export type TemplatePreviewSection = {
  key: string;
  label: string;
  currentContent?: string | null;
  templateContent?: string | null;
};

type Props = {
  sections: TemplatePreviewSection[];
  isLoading?: boolean;
  isApplying?: boolean;
  applyLabel: string;
  onClose: () => void;
  onApply: () => void;
};

type PreviewContentProps = {
  normalizedContent: string;
};

function PreviewContent({ normalizedContent }: PreviewContentProps) {
  return (
    <div
      className="ck-content text-xs"
      // oxlint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: normalizedContent }}
    />
  );
}

type NormalizedPreviewSection = TemplatePreviewSection & {
  currentContent: string;
  templateContent: string;
  hasCurrentContent: boolean;
  hasTemplateContent: boolean;
};

type PreviewSectionsProps = {
  sections: NormalizedPreviewSection[];
  isLoading: boolean;
};

function PreviewSections({ sections, isLoading }: PreviewSectionsProps) {
  const isEmpty = !isLoading && sections.length === 0;
  const hasSections = !isLoading && sections.length > 0;

  return (
    <>
      {isLoading && (
        <div className="flex h-full items-center justify-center">
          <Spinner />
        </div>
      )}
      {isEmpty && (
        <div className="flex h-full items-center justify-center text-sm text-gray-500">
          No template content to preview
        </div>
      )}
      {hasSections && (
        <div className="flex flex-col gap-3">
          {sections.map((section) => {
            const { hasCurrentContent, hasTemplateContent } = section;

            return (
              <section
                key={section.key}
                className="overflow-hidden rounded-lg border border-gray-200 bg-white"
              >
                <h3 className="border-b border-gray-200 bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-800">
                  {section.label}
                </h3>
                {hasCurrentContent && (
                  <div className={clsx('px-3 pt-2', { 'pb-2': !hasTemplateContent })}>
                    <PreviewContent normalizedContent={section.currentContent} />
                  </div>
                )}
                {hasTemplateContent && (
                  <div className={clsx('bg-blue-50 px-3 pb-2', { 'pt-2': !hasCurrentContent })}>
                    <PreviewContent normalizedContent={section.templateContent} />
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}

export default function TemplatePreviewDialog({
  sections,
  isLoading = false,
  isApplying = false,
  applyLabel,
  onClose,
  onApply,
}: Props) {
  const visibleSections = useMemo(() => sections.map((section) => {
    // Normalize (including HTML sanitization) once, then reuse for visibility and insertion.
    const currentContent = normalizeContent(section.currentContent ?? '');
    const templateContent = normalizeContent(section.templateContent ?? '');
    return {
      ...section,
      currentContent,
      templateContent,
      hasCurrentContent: !isEmptyRichText(currentContent),
      hasTemplateContent: !isEmptyRichText(templateContent),
    };
  }).filter(({ hasCurrentContent, hasTemplateContent }) => (
    hasCurrentContent || hasTemplateContent
  )), [sections]);

  return (
    <Dialog
      open
      title="Template Preview"
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="!h-[38rem] max-h-[88vh] !w-full !space-y-0 gap-3 !overflow-hidden"
      classes={{ bodyRoot: '!max-w-[52rem] !overflow-hidden' }}
    >
      <p className="shrink-0 text-xs text-gray-500">
        Existing note content appears first. Content added from the template is highlighted below it.
      </p>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <PreviewSections sections={visibleSections} isLoading={isLoading} />
      </div>
      <div className="flex shrink-0 justify-between gap-2">
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          disabled={isLoading || isApplying || visibleSections.length === 0}
          onClick={onApply}
        >
          {isApplying ? 'Applying...' : applyLabel}
        </Button>
      </div>
    </Dialog>
  );
}
